import clsx from 'clsx';
import Button from 'components/atoms/Button/Button';
import TextInput from 'components/atoms/TextInput/TextInput';
import Txt from 'components/atoms/Txt/Txt';
import CurrentEarningsCard from 'components/molecules/CurrentEarningsCard/CurrentEarningsCard';
import RedemptionFacets from 'components/molecules/RedemptionFacets/RedemptionFacets';
import { RedemptionType } from 'components/molecules/RedemptionTypesButtons/RedemptionTypesButtons';
import RedemptionTypesCard from 'components/molecules/RedemptionTypesCard/RedemptionTypesCard';
import useQueryStringParams from 'hooks/useQueryStringParams';
import React from 'react';
import { MdFilterList, MdSearch } from 'react-icons/md';
import { graphql, useLazyLoadQuery } from 'react-relay';
import { RedemptionTypeKey, RewardOptionsQuery } from './__generated__/RewardOptionsQuery.graphql';
import OptionsPane from './OptionsPane';
import classes from './RewardOptions.module.scss';

export type Props = {};

const query = graphql`
  query RewardOptionsQuery($redemptionType: RedemptionTypeKey!, $redirectUrl: String!) {
    walletBalance {
      ...CurrentEarningsCard_currentEarnings
    }
    redemptionTypes {
      ...RedemptionFacets_redemptionTypes
      types {
        name
        linkNewAccountUrl(input: { redirectUrl: $redirectUrl })
        status
      }
    }
    redemptionOptions(input: { redemptionType: $redemptionType }) {
      ...RedemptionOptions_redemptionOptionsRef
    }
  }
`;

export type Filters = { type: RedemptionTypeKey; points: string[]; search: string };

export default function RewardOptions({}: Props) {
  const { redemptionType } = useQueryStringParams();
  const [filters, setFilters] = React.useState<Partial<Filters>>({
    type: (redemptionType?.toUpperCase() as RedemptionTypeKey) || 'GIFT_CARD',
  });
  const [filtersOpen, setFiltersOpen] = React.useState(true);
  const typeName = filters.type || 'GIFT_CARD';
  const data = useLazyLoadQuery<RewardOptionsQuery>(query, {
    redemptionType: typeName,
    redirectUrl: window.location.href,
  });

  function handleRedemptionTypeSelect(type: RedemptionType) {
    setFilters((filters) => {
      return {
        ...filters,
        type: type.name,
      };
    });
  }

  function handleSearchInput(e: React.ChangeEvent<HTMLInputElement>) {
    setFilters((filters) => {
      return {
        ...filters,
        search: e.target.value,
      };
    });
  }

  function handleFiltersChange(newFilters: Partial<Filters>) {
    setFilters((filters) => {
      return { ...filters, ...newFilters };
    });
  }

  const currentRedemptionType = data.redemptionTypes?.types.find((type) => type.name === typeName);

  return (
    <div
      className={clsx(
        classes.root,
        filters.type && classes.typeChosen,
        filtersOpen ? classes.filtersOpen : classes.filtersClosed,
      )}
    >
      {filters.type ? (
        <>
          <div>
            {data.redemptionTypes && (
              <RedemptionFacets
                className={clsx(filtersOpen ? classes.filtersShow : classes.filtersHide)}
                initialType={filters.type}
                redemptionTypesRef={data.redemptionTypes}
                onFiltersChange={handleFiltersChange}
                onFiltersApply={() => setFiltersOpen(false)}
              />
            )}
          </div>
          <div>
            <div className={classes.row}>
              <TextInput
                id="search"
                placeholder="search"
                className={classes.searchInputContainer}
                inputClassName={classes.searchInput}
                startAdornment={<MdSearch className={classes.searchIcon} />}
                startAdornmentClassName={classes.searchIconContainer}
                onInput={handleSearchInput}
              />
              <Button
                buttonType="text"
                className={classes.filtersButton}
                onClick={() => setFiltersOpen((open) => !open)}
              >
                <MdFilterList />
                <Txt>Filter</Txt>
              </Button>
            </div>
            <OptionsPane
              redemptionOptions={data.redemptionOptions}
              filters={filters}
              currentRedemptionType={currentRedemptionType}
            />
          </div>
        </>
      ) : (
        <>
          <CurrentEarningsCard
            currentEarningsRef={data.walletBalance}
            buttonText={'View transactions'}
            buttonHref={'/rewards'}
            buttonAlwaysEnabled={true}
          />
          <RedemptionTypesCard onRedemptionTypeSelect={handleRedemptionTypeSelect} />
        </>
      )}
    </div>
  );
}
